import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@store/authStore'
import DashboardLayout from '@components/Layout/DashboardLayout'
import Login from '@pages/Login'
import ScannerLogin from '@pages/ScannerLogin'
import Dashboard from '@pages/Dashboard'
import Submissions from '@pages/Submissions'
import SubmissionDetail from '@pages/SubmissionDetail'
import Departments from '@pages/Departments'
import Forms from '@pages/Forms'
import Settings from '@pages/Settings'
import Scanner from '@pages/Scanner'
import PendingApprovals from '@pages/PendingApprovals'
import Registrations from '@pages/Registrations'
import PublicForm from '@pages/PublicForm'
import NotFound from '@pages/NotFound'
import EndUserLogin from '@pages/EndUserLogin'
import EndUserDashboard from '@pages/EndUserDashboard'
import EndUserSubmissionDetail from '@pages/EndUserSubmissionDetail'
import EndUserSettings from '@pages/EndUserSettings'
import EndUserLayout from '@components/Layout/EndUserLayout'
import ScannerLayout from '@components/Layout/ScannerLayout'
import EmailManagement from '@pages/EmailManagement'
import DepartmentUsers from '@pages/DepartmentUsers'
import MyDepartment from '@pages/MyDepartment'
import DirectSubmissions from '@pages/DirectSubmissions'
import WhatsAppMessages from '@pages/WhatsAppMessages'
import PublicChat from '@pages/PublicChat'
import LiveChat from '@pages/LiveChat'
import ChatSubmissions from '@pages/ChatSubmissions'

/**
 * Protected Route Wrapper (Admin/Department Users Only)
 */
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, token, user } = useAuthStore()

    if (!isAuthenticated || !token) {
        return <Navigate to="/login" replace />
    }

    // Redirect end-users to their portal
    if (user?.user_type === 'end_user') {
        return <Navigate to="/portal/dashboard" replace />
    }

    // Redirect scanner users to their scanner page
    if (user?.user_type === 'scanner') {
        return <Navigate to="/ss/scanner" replace />
    }

    return children
}

/**
 * Role-Based Route Guard - checks if user has required permissions
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child component to render
 * @param {string[]} props.allowedRoles - Array of allowed user_types (e.g., ['admin', 'form_manager'])
 * @param {Function} props.customCheck - Optional custom check function that receives user object
 */
const RoleGuard = ({ children, allowedRoles = [], customCheck = null }) => {
    const { user } = useAuthStore()

    // Check if user role is allowed
    const hasAllowedRole = allowedRoles.length === 0 || allowedRoles.includes(user?.user_type)

    // Run custom check if provided
    const passesCustomCheck = customCheck ? customCheck(user) : true

    if (!hasAllowedRole || !passesCustomCheck) {
        return <Navigate to="/dashboard" replace />
    }

    return children
}

/**
 * Scanner Protected Route Wrapper (Scanner Users Only)
 */
const ScannerProtectedRoute = ({ children }) => {
    const { isAuthenticated, token, user } = useAuthStore()

    if (!isAuthenticated || !token) {
        return <Navigate to="/ss/login" replace />
    }

    // Non-scanner users can't access this
    if (user?.user_type !== 'scanner' && user?.user_type !== 'admin') {
        return <Navigate to="/dashboard" replace />
    }

    return children
}

/**
 * End-User Protected Route Wrapper
 */
const EndUserProtectedRoute = ({ children }) => {
    const { isAuthenticated, token, user } = useAuthStore()

    if (!isAuthenticated || !token) {
        return <Navigate to="/portal/login" replace />
    }

    // Ensure this is an end-user
    if (user?.user_type !== 'end_user') {
        return <Navigate to="/dashboard" replace />
    }

    return children
}

/**
 * Application Routes
 */
const AppRoutes = () => {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/form/:formId" element={<PublicForm />} />
            <Route path="/chat/:formId" element={<PublicChat />} />

            {/* Scanner Routes */}
            <Route path="/ss/login" element={<ScannerLogin />} />
            <Route
                path="/ss/scanner"
                element={
                    <ScannerProtectedRoute>
                        <ScannerLayout>
                            <Scanner />
                        </ScannerLayout>
                    </ScannerProtectedRoute>
                }
            />

            {/* End-User Portal Routes */}
            <Route path="/portal/login" element={<EndUserLogin />} />
            <Route
                path="/portal"
                element={
                    <EndUserProtectedRoute>
                        <EndUserLayout />
                    </EndUserProtectedRoute>
                }
            >
                <Route path="dashboard" element={<EndUserDashboard />} />
                <Route path="submission/:id" element={<EndUserSubmissionDetail />} />
                <Route path="settings" element={<EndUserSettings />} />
            </Route>

            {/* Protected Dashboard Routes */}
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <DashboardLayout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<Navigate to="/dashboard" replace />} />

                {/* Dashboard - all authenticated users */}
                <Route path="dashboard" element={<Dashboard />} />

                {/* Submissions - admin, form_manager, dashboard, department */}
                <Route path="submissions" element={<Submissions />} />
                <Route path="submissions/:id" element={<SubmissionDetail />} />

                {/* Direct Submissions - admin only OR dashboard users with enable_direct_submissions */}
                <Route
                    path="direct-submissions"
                    element={
                        <RoleGuard
                            allowedRoles={[]}
                            customCheck={(user) => user?.user_type === 'admin' || user?.user_type === 'form_manager' || (user?.user_type === 'dashboard' && user?.enable_direct_submissions)}
                        >
                            <DirectSubmissions />
                        </RoleGuard>
                    }
                />

                {/* Pending Approvals - department, dashboard, form_manager */}
                <Route
                    path="pending-approvals"
                    element={
                        <RoleGuard allowedRoles={['admin', 'department', 'dashboard', 'form_manager']}>
                            <PendingApprovals />
                        </RoleGuard>
                    }
                />

                {/* Forms - admin, form_manager only */}
                <Route
                    path="forms"
                    element={
                        <RoleGuard allowedRoles={['admin', 'form_manager']}>
                            <Forms />
                        </RoleGuard>
                    }
                />

                {/* Departments - admin only */}
                <Route
                    path="departments"
                    element={
                        <RoleGuard allowedRoles={['admin']}>
                            <Departments />
                        </RoleGuard>
                    }
                />

                {/* Registrations - admin only */}
                <Route
                    path="registrations"
                    element={
                        <RoleGuard allowedRoles={['admin']}>
                            <Registrations />
                        </RoleGuard>
                    }
                />

                {/* Settings - admin only */}
                <Route
                    path="settings"
                    element={
                        <RoleGuard allowedRoles={['admin']}>
                            <Settings />
                        </RoleGuard>
                    }
                />

                {/* Email Management - admin only */}
                <Route
                    path="emails"
                    element={
                        <RoleGuard allowedRoles={['admin']}>
                            <EmailManagement />
                        </RoleGuard>
                    }
                />

                {/* WhatsApp Messages - admin, form_manager, dashboard */}
                <Route
                    path="whatsapp"
                    element={
                        <RoleGuard allowedRoles={['admin', 'form_manager', 'dashboard']}>
                            <WhatsAppMessages />
                        </RoleGuard>
                    }
                />

                {/* Live Chat - admin, form_manager, dashboard */}
                <Route
                    path="live-chat"
                    element={
                        <RoleGuard allowedRoles={['admin', 'form_manager', 'dashboard']}>
                            <LiveChat />
                        </RoleGuard>
                    }
                />

                {/* Chat Submissions - admin, form_manager, dashboard */}
                <Route
                    path="chat-submissions"
                    element={
                        <RoleGuard allowedRoles={['admin', 'form_manager', 'dashboard']}>
                            <ChatSubmissions />
                        </RoleGuard>
                    }
                />

                {/* My Department - department users */}
                <Route
                    path="my-department"
                    element={
                        <RoleGuard allowedRoles={['department']}>
                            <MyDepartment />
                        </RoleGuard>
                    }
                />

                {/* Department Users - department leaders only */}
                <Route
                    path="department-users"
                    element={
                        <RoleGuard
                            allowedRoles={['department']}
                            customCheck={(user) => user?.is_department_leader}
                        >
                            <DepartmentUsers />
                        </RoleGuard>
                    }
                />

                {/* Scanner - admin, form_manager only */}
                <Route
                    path="scanner"
                    element={
                        <RoleGuard allowedRoles={['admin', 'form_manager']}>
                            <Scanner />
                        </RoleGuard>
                    }
                />
            </Route>

            {/* 404 Not Found */}
            <Route path="*" element={<NotFound />} />
        </Routes>
    )
}

export default AppRoutes
